import { matchPath } from "react-router-dom";
import { z } from "zod";
import { UserTypeEnum } from "../types/common";

export type UserType = z.infer<typeof UserTypeEnum>;

const ALL_USER_TYPES = UserTypeEnum.options;

export type RoutePolicy = {
  pattern: string;
  roles: UserType[];
  allowedUserIds: string[];
};

export type NavMenuItemDef = {
  id: string;
  label: string;
  path: string;
  navigateTo?: string;
  order: number;
  roles: UserType[];
  allowedUserIds: string[];
};

const S = {
  ownerAdmin: ["owner", "admin"] as UserType[],
  ownerAdminEmployee: ["owner", "admin", "employee"] as UserType[],
  ownerAppAdmin: ["owner", "app_admin"] as UserType[],
  ledgerFinance: ["owner", "app_admin"] as UserType[],
  warehouse: [
    "owner",
    "admin",
    "employee",
    "warehouse_employee",
  ] as UserType[],
  dispatch: ["owner", "admin", "dispatch_employee"] as UserType[],
  adminMgmt: ["owner", "admin", "app_admin"] as UserType[],
  tasksAll: ALL_USER_TYPES,
};

const ROUTE_POLICIES: RoutePolicy[] = [
  {
    pattern: "/userManagement",
    roles: S.adminMgmt,
    allowedUserIds: [],
  },
  { pattern: "/clients/sites", roles: S.ownerAdminEmployee, allowedUserIds: [] },
  { pattern: "/clients", roles: S.ownerAdminEmployee, allowedUserIds: [] },
  {
    pattern: "/clientName/:name",
    roles: S.ownerAdminEmployee,
    allowedUserIds: [],
  },
  {
    pattern: "/clientProject/:siteName",
    roles: S.ownerAdminEmployee,
    allowedUserIds: [],
  },
  { pattern: "/sku", roles: S.ownerAdminEmployee, allowedUserIds: [] },
  { pattern: "/ledger", roles: S.ledgerFinance, allowedUserIds: [] },
  { pattern: "/quotation/create", roles: S.ownerAdminEmployee, allowedUserIds: [] },
  { pattern: "/quotation/edit", roles: S.ownerAdminEmployee, allowedUserIds: [] },
  { pattern: "/quotation/costPrice", roles: S.ownerAdminEmployee, allowedUserIds: [] },
  { pattern: "/quotation", roles: S.ownerAdminEmployee, allowedUserIds: [] },
  { pattern: "/openQuotes", roles: S.ledgerFinance, allowedUserIds: [] },
  { pattern: "/payments", roles: S.ledgerFinance, allowedUserIds: [] },
  {
    pattern: "/accounts",
    roles: ["owner", "admin", "app_admin", "employee"],
    allowedUserIds: [],
  },
  { pattern: "/retail/edit/:quoteId", roles: S.ownerAdmin, allowedUserIds: [] },
  { pattern: "/retail/create", roles: S.ownerAdmin, allowedUserIds: [] },
  { pattern: "/retail-quote", roles: S.ownerAdmin, allowedUserIds: [] },
  { pattern: "/boq/create", roles: S.ownerAdminEmployee, allowedUserIds: [] },
  { pattern: "/boq/edit", roles: S.ownerAdminEmployee, allowedUserIds: [] },
  { pattern: "/purchaseOrder", roles: S.ownerAdminEmployee, allowedUserIds: [] },
  {
    pattern: "/deliveryChallan/create",
    roles: S.ownerAdminEmployee,
    allowedUserIds: [],
  },
  {
    pattern: "/deliveryChallan/edit",
    roles: S.ownerAdminEmployee,
    allowedUserIds: [],
  },
  { pattern: "/deliveryChallan", roles: S.ownerAdminEmployee, allowedUserIds: [] },
  { pattern: "/create/replacement", roles: S.ownerAdminEmployee, allowedUserIds: [] },
  { pattern: "/replacementDC", roles: S.ownerAdminEmployee, allowedUserIds: [] },
  {
    pattern: "/warehouse/purchaseOrder",
    roles: S.warehouse,
    allowedUserIds: [],
  },
  {
    pattern: "/pendingReplacement",
    roles: S.warehouse,
    allowedUserIds: [],
  },
  { pattern: "/dc/dispatch", roles: S.dispatch, allowedUserIds: [] },
  { pattern: "/sampleDC", roles: S.ownerAdminEmployee, allowedUserIds: [] },
  {
    pattern: "/sales-return/create",
    roles: S.ownerAdminEmployee,
    allowedUserIds: [],
  },
  {
    pattern: "/sales-return/duplicate",
    roles: S.ownerAdminEmployee,
    allowedUserIds: [],
  },
  { pattern: "/tasks", roles: S.tasksAll, allowedUserIds: [] },
  { pattern: "/create/sampleDC", roles: S.ownerAdminEmployee, allowedUserIds: [] },
  { pattern: "/site-config", roles: S.ownerAdmin, allowedUserIds: [] },
  { pattern: "/closedProjects", roles: S.ownerAdmin, allowedUserIds: [] },
  { pattern: "/sales", roles: S.ownerAdmin, allowedUserIds: [] },
  { pattern: "/expenses", roles: S.ownerAdmin, allowedUserIds: [] },
  { pattern: "/petty-cash", roles: S.ownerAdmin, allowedUserIds: [] },
  { pattern: "/dashboard", roles: S.ownerAdmin, allowedUserIds: [] },
];

const SORTED_ROUTE_POLICIES = [...ROUTE_POLICIES].sort(
  (a, b) => b.pattern.length - a.pattern.length
);

export function allowlistAllowsUser(
  allowedUserIds: string[] | undefined,
  userId: string | null | undefined
): boolean {
  const list = allowedUserIds ?? [];
  if (list.length === 0) {
    return true;
  }
  if (!userId) {
    return false;
  }
  return list.includes(userId);
}

export const NAV_MENU_ITEMS: NavMenuItemDef[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    path: "/dashboard",
    order: 10,
    roles: S.ownerAdmin,
    allowedUserIds: [],
  },
  {
    id: "create-boq",
    label: "Create BOQ",
    path: "/boq/create",
    order: 20,
    roles: S.ownerAdmin,
    allowedUserIds: [],
  },
  {
    id: "retail",
    label: "Retail",
    path: "/retail-quote",
    order: 30,
    roles: S.ownerAdmin,
    allowedUserIds: [],
  },
  {
    id: "clients-owner",
    label: "Clients",
    path: "/clients",
    order: 40,
    roles: S.ownerAdmin,
    allowedUserIds: [],
  },
  {
    id: "closed-projects",
    label: "Closed Projects",
    path: "/closedProjects",
    navigateTo: "/closedProjects?closed=true",
    order: 45,
    roles: S.ownerAdmin,
    allowedUserIds: [],
  },
  {
    id: "sku-owner",
    label: "SKU",
    path: "/sku",
    order: 50,
    roles: S.ownerAdmin,
    allowedUserIds: [],
  },
  {
    id: "site-config",
    label: "Site Configuration",
    path: "/site-config",
    order: 60,
    roles: S.ownerAdmin,
    allowedUserIds: [],
  },
  {
    id: "sales",
    label: "Sales",
    path: "/sales",
    order: 70,
    roles: S.ownerAdmin,
    allowedUserIds: [],
  },
  {
    id: "expenses",
    label: "Expenses",
    path: "/expenses",
    order: 80,
    roles: S.ownerAdmin,
    allowedUserIds: [],
  },
  {
    id: "petty-cash",
    label: "Petty Cash",
    path: "/petty-cash",
    order: 90,
    roles: S.ownerAdmin,
    allowedUserIds: [],
  },
  {
    id: "quotation",
    label: "Quotation",
    path: "/quotation",
    order: 10,
    roles: ["employee"],
    allowedUserIds: [],
  },
  {
    id: "clients-employee",
    label: "Clients",
    path: "/clients",
    order: 20,
    roles: ["employee"],
    allowedUserIds: [],
  },
  {
    id: "sku-employee",
    label: "SKU",
    path: "/sku",
    order: 30,
    roles: ["employee"],
    allowedUserIds: [],
  },
  {
    id: "sample-dc",
    label: "Sample DC",
    path: "/sampleDC",
    order: 40,
    roles: ["employee"],
    allowedUserIds: [],
  },
  {
    id: "warehouse-po",
    label: "Purchase Orders",
    path: "/warehouse/purchaseOrder",
    order: 10,
    roles: ["warehouse_employee"],
    allowedUserIds: [],
  },
  {
    id: "dispatch-dc",
    label: "Dispatch DC",
    path: "/dc/dispatch",
    order: 10,
    roles: ["dispatch_employee"],
    allowedUserIds: [],
  },
  {
    id: "tasks",
    label: "Tasks",
    path: "/tasks",
    order: 200,
    roles: ALL_USER_TYPES,
    allowedUserIds: [],
  },
  {
    id: "ledger",
    label: "Ledger",
    path: "/ledger",
    order: 210,
    roles: S.ledgerFinance,
    allowedUserIds: [],
  },
  {
    id: "pending-payments",
    label: "Pending Payments",
    path: "/openQuotes",
    order: 220,
    roles: S.ledgerFinance,
    allowedUserIds: [],
  },
  // {
  //   id: "payments",
  //   label: "Payments",
  //   path: "/payments",
  //   order: 230,
  //   roles: S.ledgerFinance,
  //   allowedUserIds: [],
  // },
];

function effectiveUserIdsForRoute(
  rule: RoutePolicy,
  pathname: string,
  ut: UserType
): string[] {
  if (rule.allowedUserIds.length > 0) {
    return rule.allowedUserIds;
  }
  const navItem = NAV_MENU_ITEMS.find(
    (n) => n.path === pathname && n.roles.includes(ut)
  );
  return navItem?.allowedUserIds ?? [];
}

export function getMenuItemsForRole(
  userType: string | null | undefined,
  userId: string | null | undefined
): { id: string; label: string; path: string; navigateTo?: string }[] {
  if (!userType || !UserTypeEnum.safeParse(userType).success) {
    return [];
  }
  const ut = userType as UserType;
  return NAV_MENU_ITEMS.filter(
    (item) =>
      item.roles.includes(ut) && allowlistAllowsUser(item.allowedUserIds, userId)
  )
    .sort((a, b) => a.order - b.order)
    .map(({ id, label, path, navigateTo }) => ({
      id,
      label,
      path,
      ...(navigateTo ? { navigateTo } : {}),
    }));
}

export function getDefaultRedirectPath(
  userType: string | null | undefined,
  userId: string | null | undefined
): string {
  const items = getMenuItemsForRole(userType, userId);
  if (items.length > 0) {
    return items[0].path;
  }
  return "/tasks";
}

export function canAccessPath(
  pathname: string,
  userType: string | null | undefined,
  userId: string | null | undefined
): boolean {
  if (!userType || !UserTypeEnum.safeParse(userType).success) {
    return false;
  }
  const ut = userType as UserType;
  for (const rule of SORTED_ROUTE_POLICIES) {
    const m = matchPath({ path: rule.pattern, end: true }, pathname);
    if (m) {
      const userIds = effectiveUserIdsForRoute(rule, pathname, ut);
      return (
        rule.roles.includes(ut) && allowlistAllowsUser(userIds, userId)
      );
    }
  }
  return false;
}
